import {
  getSessionStorageItem,
  removeSessionStorageItem,
  setSessionStorageItem,
} from "common/utils/storage/session-storage";
import {
  CURRENT,
  REDO,
  REDO_ALLOCATED,
  REDO_UNALLOCATED,
  UNDO,
  UNDO_ALLOCATED,
  UNDO_UNALLOCATED,
} from "features/budget/budget.constants";
import type { useSaveEmployeeRow } from "./handle-employee";
import type useSaveEmployeeAllocation from "./use-save-employee-allocation";
import { getEmployeeData } from "./use-save-employee-allocation";

type UnAllocatedType = {
  action: string;
  data: [
    {
      budgetScenarioEmployeeAllocationId: string;
      orgDetailId: number;
      scenarioId: string;
    },
  ];
};

type UndoRedoType = {
  action: string;
  data: [{ allocation: number; employeeRecordId: string; orgDetailId: number }];
};

type EmployeesAllocateType = {
  employeeRecordId: number;
  orgDetailId: number;
  allocation: number;
};

const getValue = (value: number) => {
  if (value < 1) {
    return value * 100;
  }
  return value / 100;
};

const getEmployeesAllocated = (employeesInfo: EmployeesAllocateType[]) => {
  const employeesData = employeesInfo.map(
    ({ employeeRecordId, allocation, orgDetailId }) => ({
      employeeRecordId,
      allocation: getValue(allocation),
      orgDetailId: Number(orgDetailId),
    }),
  );

  return { data: employeesData };
};

export const removeSessionStorageUNDO = () => {
  removeSessionStorageItem(UNDO);
};

export const removeSessionStorageREDO = () => {
  removeSessionStorageItem(REDO);
};

export const removeSessionStorageCURRENT = () => {
  removeSessionStorageItem(CURRENT);
};

export const removeAllSessionStorageForUndoRedo = () => {
  removeSessionStorageUNDO();
  removeSessionStorageREDO();
  removeSessionStorageCURRENT();
};

export const getSessionStorageCURRENT: any = () => {
  const current = getSessionStorageItem(CURRENT);

  return JSON.parse(current!);
};

/**
 * Every time an employee is un-allocated either with the deallocate button or undo button, this method creates a sessionStorage object with the information of the un-allocated employee
 */
export const createSessionStorageOnUnAllocatedEmployee = (
  employeeRecordId: string,
  organizationId: number,
  allocation: number,
  scenarioId: string,
) => {
  setSessionStorageItem(
    UNDO,
    JSON.stringify({
      data: [{ employeeRecordId, orgDetailId: organizationId, allocation }],
      action: UNDO_UNALLOCATED,
    }),
  );

  setSessionStorageItem(
    CURRENT,
    JSON.stringify({
      data: [{ employeeRecordId, orgDetailId: organizationId, scenarioId }],
      action: REDO_UNALLOCATED,
    }),
  );

  removeSessionStorageItem(REDO);
};

/**
 * Every time an employee record is un-allocated, the allocation percentage is updated, or allocated to a node either with the drag and drop functionality or with the redo button, this method creates a sessionStorage object with the information of the allocated employee
 */
export const createSessionStorageAllocatedEmployees = (
  employees: [
    { employeeRecordId: number; orgDetailId: number; allocation: number },
  ],
  sessionStorageKey = UNDO,
) => {
  const allocatedEmployees = getEmployeesAllocated(employees);
  const action = sessionStorageKey === UNDO ? UNDO_ALLOCATED : REDO_ALLOCATED;

  const info = { action, ...allocatedEmployees };

  setSessionStorageItem(sessionStorageKey, JSON.stringify(info));
};

/**
 * When an employee record has been assigned to more nodes, this method takes the information from the sessionStorage object and changes the allocations percentage
 */
export const undoAllocatedEmployee = (
  saveAllocations: ReturnType<typeof useSaveEmployeeAllocation>,
) => {
  const undo = getSessionStorageItem(UNDO);
  const currValue = getSessionStorageItem(CURRENT);
  const allocatedEmployeeInfo: UndoRedoType = JSON.parse(undo!);

  saveAllocations.mutate(getEmployeeData(allocatedEmployeeInfo.data));

  removeSessionStorageItem(UNDO);
  removeSessionStorageItem(REDO);

  setSessionStorageItem(REDO, currValue);
  setSessionStorageItem(CURRENT, undo);
};

export const redoAllocatedEmployee = (
  saveAllocations: ReturnType<typeof useSaveEmployeeAllocation>,
) => {
  const redoInfo = getSessionStorageItem(REDO);
  const unAllocatedEmployeeInfo: UndoRedoType = JSON.parse(redoInfo!);

  saveAllocations.mutate(getEmployeeData(unAllocatedEmployeeInfo.data));
  removeSessionStorageItem(REDO);

  const undo = getSessionStorageItem(CURRENT);
  setSessionStorageItem(UNDO, undo);
  setSessionStorageItem(CURRENT, redoInfo);
};

/**
 * Be aware that an employee record can be un-allocated with the deallocate buttons or with the redo button
 * if it's been previously reallocated
 */
export const undoUnAllocatedEmployee = (
  saveAllocations: ReturnType<typeof useSaveEmployeeAllocation>,
) => {
  const undo = getSessionStorageItem(UNDO);

  const allocatedEmployeeInfo: UndoRedoType = JSON.parse(undo!);

  saveAllocations.mutate(getEmployeeData(allocatedEmployeeInfo.data));

  const redo = getSessionStorageItem(CURRENT);

  removeSessionStorageItem(UNDO);

  setSessionStorageItem(REDO, redo);
  setSessionStorageItem(CURRENT, undo);
};

/**
 * Deallocate an employee record
 *
 * When an employee record has been reallocated, this method takes the
 * information from the sessionStorage object and deallocate the employee record
 */
export const redoUnAllocateEmployee = (
  saveEmployee: ReturnType<typeof useSaveEmployeeRow>,
) => {
  const redoInfo = getSessionStorageItem(REDO);
  const unAllocatedEmployeeInfo: UnAllocatedType = JSON.parse(redoInfo!);

  const {
    budgetScenarioEmployeeAllocationId,
    orgDetailId: organizationId,
    scenarioId,
  } = unAllocatedEmployeeInfo.data[0];

  saveEmployee.mutate({
    scenarioId,
    organizationId,
    budgetScenarioEmployeeAllocationId,
  });
  removeSessionStorageItem(REDO);

  const undo = getSessionStorageItem(CURRENT);
  setSessionStorageItem(UNDO, undo);
  setSessionStorageItem(CURRENT, redoInfo);
};

export const ACTION_TO_PERFORM = {
  UNDO_ALLOCATED: (
    saveAllocations: ReturnType<typeof useSaveEmployeeAllocation>,
  ) => undoAllocatedEmployee(saveAllocations),
  REDO_ALLOCATED: (
    saveAllocations: ReturnType<typeof useSaveEmployeeAllocation>,
  ) => redoAllocatedEmployee(saveAllocations),
  UNDO_UNALLOCATED: (
    saveAllocations: ReturnType<typeof useSaveEmployeeAllocation>,
  ) => undoUnAllocatedEmployee(saveAllocations),
  REDO_UNALLOCATED: (saveEmployee: ReturnType<typeof useSaveEmployeeRow>) =>
    redoUnAllocateEmployee(saveEmployee),
};
